import type { AxiosRequestConfig } from 'axios'
import API from '../apiCreate'
import { ResponseWrapper } from '../types/base.types'

export type SupportTicketStatus = 'open' | 'answered' | 'closed'

// Empty string disables a scenario; an absent field keeps the legacy fallback.
export type SupportTexts = Partial<Record<'lead' | 'aiLead' | 'greeting' | 'aiGreeting' | 'taken' | 'escalated' | 'closed' | 'offline' | 'offlineAi', string>>
export interface SupportWorkingHours { enabled: boolean; open: boolean; label: string }

export interface SupportTicketSection {
  id: number
  name: string
  // Новые обращения раздела сначала получает ИИ-ассистент
  ai?: boolean
  texts?: SupportTexts
}

export interface SupportCallbackState {
  limit: number
  used: number
  remaining: number
  pending: boolean
  nextResetAt: number
}

export interface SupportTicketState {
  callback?: SupportCallbackState | null
  available: boolean
  sections: SupportTicketSection[]
  unread: number
  workingHours?: SupportWorkingHours
  aiOnly?: boolean
  specialistNotice?: string
  announcement?: { enabled: boolean; pending: boolean }
}

export interface SupportTicketSummary {
  id: number
  section: string
  status: SupportTicketStatus
  createdAt: number
  updatedAt: number
  unread: boolean
  lastMessage: { body: string; fromSupport: boolean }
}

export interface SupportTicketAttachment {
  url: string
  name: string
}

export interface SupportTaskProposal {
  id: number
  title: string
  description: string
  price: string
  deadline: string
  paymentStatus: 'unpaid' | 'paid'
  workStatus: 'in_progress' | 'completed'
  decisionStatus: 'pending' | 'accepted' | 'declined'
  decisionAt: number | null
}

export interface SupportTicketMessage {
  id: number
  fromSupport: boolean
  authorName: string
  body: string
  createdAt: number
  attachments?: SupportTicketAttachment[]
  fromAi?: boolean
  isQuestion?: boolean
  feedback?: { rating: 'good' | 'bad'; comment: string } | null
  taskProposal?: SupportTaskProposal | null
}

export interface SupportTicketThread {
  id: number
  section: string
  status: SupportTicketStatus
  createdAt: number
  updatedAt: number
  // ai — отвечает ИИ-ассистент, human — специалист
  isCallback?: boolean
  handler?: 'ai' | 'human'
  escalated?: boolean
  texts?: SupportTexts
  workingHours?: SupportWorkingHours
  aiAnswered?: boolean
  replyBlockReason?: 'ai_pending' | 'ai_decision' | 'specialist_pending' | null
  aiOnly?: boolean
  messages: SupportTicketMessage[]
}

// Ошибки показываем внутри окна техподдержки, а не глобальным алертом:
// фоновый опрос не должен сыпать уведомлениями, а ошибки отправки видны у формы.
const quiet = { skipGlobalErrorAlert: true } as AxiosRequestConfig & { skipGlobalErrorAlert?: boolean }

export const supportTicketApi = {
  state: () => API.get<ResponseWrapper<SupportTicketState>>('support-ticket/state', quiet),
  requestCallback: () => API.post<ResponseWrapper<SupportCallbackState>>('support-ticket/request-callback', {}, quiet),
  claimAnnouncement: () => API.post<ResponseWrapper<{ show: boolean }>>('support-ticket/announcement-claim', {}, quiet),
  list: () => API.get<ResponseWrapper<SupportTicketSummary[]>>('support-ticket/index', quiet),
  view: (id: number) =>
    API.get<ResponseWrapper<SupportTicketThread>>('support-ticket/view', { ...quiet, params: { id } }),
  create: (sectionId: number, message: string, images: File[] = []) =>
    send('support-ticket/create', { sectionId, message }, images),
  reply: (id: number, message: string, images: File[] = []) =>
    send('support-ticket/reply', { id, message }, images),
  escalate: (id: number) =>
    API.post<ResponseWrapper<SupportTicketThread>>('support-ticket/escalate', { id }, quiet),
  close: (id: number) =>
    API.post<ResponseWrapper<SupportTicketThread>>('support-ticket/close', { id }, quiet),
  feedback: (id: number, messageId: number, rating: 'good' | 'bad', comment: string) =>
    API.post<ResponseWrapper<SupportTicketThread>>('support-ticket/feedback', { id, messageId, rating, comment }, quiet),
  decideTask: (id: number, decision: 'accepted' | 'declined') =>
    API.post<ResponseWrapper<SupportTicketThread>>('support-ticket/task-decision', { id, decision }, quiet),
}

// Без картинок — обычный JSON; с картинками — multipart с полем images[], как у загрузок чата
function send(url: string, fields: Record<string, string | number>, images: File[]) {
  if (!images.length) return API.post<ResponseWrapper<SupportTicketThread>>(url, fields, quiet)
  const form = new FormData()
  Object.entries(fields).forEach(([key, value]) => form.append(key, String(value)))
  images.forEach((image) => form.append('images[]', image, image.name || 'image.png'))
  return API.post<ResponseWrapper<SupportTicketThread>>(url, form, { ...quiet, headers: { 'Content-type': 'multipart/form-data' } })
}
